/**
 * Tarjeta de un aviso individual. Reusada en el listado (/avisos) y en el
 * dropdown de la campana (versión compacta con `compact={true}`).
 */
import {
  AlertCircle,
  Building2,
  CheckCircle2,
  Clock,
  Handshake,
  Home,
  Lock,
  Pencil,
  Phone,
  ShieldCheck,
  User,
  UserCircle2,
  X,
  Zap,
} from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';

import type { Aviso } from '../types';
import { ESTADO_LABELS, TIPO_LABELS } from '../types';

interface AvisoCardProps {
  aviso: Aviso;
  onResolver?: (aviso: Aviso) => void;
  onDescartar?: (aviso: Aviso) => void;
  /** Si se pasa, los avisos MANUALES muestran el botón de editar. */
  onEditar?: (aviso: Aviso) => void;
  /** Versión condensada para el dropdown. */
  compact?: boolean;
}

/**
 * Los avisos automáticos no se pueden editar: el motor reescribe título y
 * descripción en cada pasada, así que el cambio se perdería esa noche. Se
 * enseña el candado con este texto para que el usuario lo sepa ANTES de
 * intentarlo, en vez de descubrirlo por un error del servidor.
 */
const MOTIVO_NO_EDITABLE = 'Generado por el sistema — no editable (se regenera automáticamente)';

const TIPO_ICONS = {
  FIN_ENCARGO: Clock,
  INMUEBLE_CAMPOS_FALTANTES: Home,
  OPERACION_CAMPOS_FALTANTES: Handshake,
  SUMINISTRO_PENDIENTE: Zap,
  SEGURO_PENDIENTE: ShieldCheck,
  MANUAL_LEAD: Building2,
} as const;

// Color del icono según el tipo. Naranja/rojo para los urgentes, gris para
// los informativos. La paleta sigue brand-tomato + tonos suaves.
const TIPO_COLORS: Record<Aviso['tipo'], string> = {
  FIN_ENCARGO: 'text-amber-600 bg-amber-50',
  INMUEBLE_CAMPOS_FALTANTES: 'text-brand-tomato bg-brand-tomato/10',
  OPERACION_CAMPOS_FALTANTES: 'text-brand-tomato bg-brand-tomato/10',
  SUMINISTRO_PENDIENTE: 'text-yellow-700 bg-yellow-50',
  SEGURO_PENDIENTE: 'text-blue-600 bg-blue-50',
  MANUAL_LEAD: 'text-emerald-600 bg-emerald-50',
};

const ESTADO_BADGE: Record<Aviso['estado'], string> = {
  PENDIENTE: 'bg-brand-tomato/10 text-brand-tomato',
  RESUELTO: 'bg-emerald-100 text-emerald-700',
  DESCARTADO: 'bg-brand-silver/30 text-brand-gray',
};

function formatDate(s: string | null | undefined): string {
  if (!s) return '';
  try {
    const d = new Date(s);
    return d.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return s;
  }
}

/** Devuelve la ruta interna a la que debería llevar el aviso al hacer click.
 *
 * TODOS los recursos llevan un parámetro con el id para que la página de
 * destino abra el registro concreto. Sin él, el usuario aterriza en un listado
 * de cientos de filas y tiene que buscar a mano justo lo que el aviso le
 * acababa de señalar (reportado por el usuario final el 28-jul-2026 sobre los
 * avisos de operación; suministros y seguros arrastraban el mismo fallo).
 *
 * El nombre del parámetro no es uniforme por motivos históricos: `?focus=` en
 * inmuebles, operaciones, suministros y seguros; `?lead=` y `?registro=` en
 * leads y prospección, que ya estaban en producción.
 */
function getLinkFromAviso(aviso: Aviso): string | null {
  if (aviso.inmueble_id) return `/inmuebles?focus=${aviso.inmueble_id}`;
  // La operación abre su SEGUIMIENTO: los campos que vigila el aviso
  // (Valor Escritura, Valor Referencia, Cambio de Suministros) están ahí, no
  // en el formulario de la operación.
  if (aviso.operacion_id) return `/operaciones?focus=${aviso.operacion_id}`;
  if (aviso.cambio_suministro_id) return `/cambio-suministros?focus=${aviso.cambio_suministro_id}`;
  if (aviso.seguro_alquiler_id) return `/seguro-alquiler?focus=${aviso.seguro_alquiler_id}`;
  if (aviso.lead_oficina_id) return `/leads-oficina?lead=${aviso.lead_oficina_id}`;
  if (aviso.lead_digital_id) return `/leads-digitales?lead=${aviso.lead_digital_id}`;
  if (aviso.prospeccion_agente_id)
    return `/prospeccion-agentes?registro=${aviso.prospeccion_agente_id}`;
  return null;
}

const AvisoCard: React.FC<AvisoCardProps> = ({
  aviso,
  onResolver,
  onDescartar,
  onEditar,
  compact = false,
}) => {
  const navigate = useNavigate();
  const Icon = TIPO_ICONS[aviso.tipo] ?? AlertCircle;
  const iconClass = TIPO_COLORS[aviso.tipo] ?? 'text-brand-gray bg-brand-ghost';
  const link = getLinkFromAviso(aviso);
  const pendiente = aviso.estado === 'PENDIENTE';
  const esAutomatico = aviso.origen === 'AUTOMATICO';

  const handleNavigate = () => {
    if (link) navigate(link);
  };

  return (
    <div
      className={`bg-white rounded-xl border border-brand-silver/30 ${
        compact ? 'p-3' : 'p-4'
      } hover:border-brand-tomato/40 transition-colors`}
    >
      <div className="flex items-start gap-3">
        <div className={`p-2 rounded-lg ${iconClass} flex-shrink-0`}>
          <Icon size={compact ? 16 : 20} aria-hidden="true" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <h4
              className={`font-semibold text-brand-dark ${
                compact ? 'text-sm' : 'text-base'
              } leading-tight`}
            >
              {aviso.titulo}
            </h4>
            <div className="flex-shrink-0 flex items-center gap-1.5">
              <span
                title={esAutomatico ? MOTIVO_NO_EDITABLE : 'Creado por una persona'}
                className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                  esAutomatico
                    ? 'bg-brand-silver/30 text-brand-gray'
                    : 'bg-emerald-100 text-emerald-700'
                }`}
              >
                {esAutomatico ? 'Automático' : 'Manual'}
              </span>
              <span
                className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                  ESTADO_BADGE[aviso.estado]
                }`}
              >
                {ESTADO_LABELS[aviso.estado]}
              </span>
            </div>
          </div>

          <p className="text-[11px] text-brand-gray uppercase tracking-wider mt-0.5">
            {TIPO_LABELS[aviso.tipo]}
          </p>

          {aviso.descripcion && (
            <p
              className={`text-brand-gray ${
                compact ? 'text-xs mt-1.5 line-clamp-2' : 'text-sm mt-2'
              }`}
            >
              {aviso.descripcion}
            </p>
          )}

          {aviso.destinatario_nombre && (
            <p
              className={`flex items-center gap-1 text-brand-gray ${
                compact ? 'mt-1.5 text-[11px]' : 'mt-2 text-xs'
              }`}
            >
              <UserCircle2 size={12} />
              <span>
                Agente:{' '}
                <span className="font-medium text-brand-dark">{aviso.destinatario_nombre}</span>
              </span>
            </p>
          )}

          {(aviso.lead_nombre || aviso.lead_telefono) && (
            <div
              className={`flex flex-wrap items-center gap-x-3 gap-y-1 ${
                compact ? 'mt-1.5 text-[11px]' : 'mt-2 text-xs'
              } text-brand-gray`}
            >
              {aviso.lead_nombre && (
                <span className="flex items-center gap-1">
                  <User size={12} />
                  <span className="font-medium text-brand-dark">{aviso.lead_nombre}</span>
                </span>
              )}
              {aviso.lead_telefono && (
                <a
                  href={`tel:${aviso.lead_telefono}`}
                  onClick={(e) => e.stopPropagation()}
                  className="flex items-center gap-1 hover:text-brand-tomato transition-colors"
                >
                  <Phone size={12} />
                  <span>{aviso.lead_telefono}</span>
                </a>
              )}
            </div>
          )}

          {!compact && aviso.fecha_evento && (
            <p className="text-xs text-brand-gray mt-2 flex items-center gap-1">
              <Clock size={12} />
              <span>Vence: {formatDate(aviso.fecha_evento)}</span>
            </p>
          )}

          {!compact && (
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              {link && (
                <button
                  type="button"
                  onClick={handleNavigate}
                  className="text-xs font-medium text-brand-tomato hover:underline"
                >
                  {aviso.lead_oficina_id ||
                  aviso.lead_digital_id ||
                  aviso.prospeccion_agente_id ||
                  aviso.operacion_id
                    ? 'Ir al seguimiento →'
                    : 'Ir al recurso →'}
                </button>
              )}
              {onEditar &&
                (esAutomatico ? (
                  <span
                    title={MOTIVO_NO_EDITABLE}
                    aria-label={MOTIVO_NO_EDITABLE}
                    className="flex items-center gap-1 text-xs px-3 py-1.5 bg-brand-ghost text-brand-gray/70 rounded-md font-medium cursor-not-allowed"
                  >
                    <Lock size={14} />
                    No editable
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => onEditar(aviso)}
                    className="flex items-center gap-1 text-xs px-3 py-1.5 bg-brand-ghost text-brand-gray hover:bg-brand-silver/30 rounded-md transition-colors font-medium"
                  >
                    <Pencil size={14} />
                    Editar
                  </button>
                ))}
              {pendiente && onResolver && (
                <button
                  type="button"
                  onClick={() => onResolver(aviso)}
                  className="flex items-center gap-1 text-xs px-3 py-1.5 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 rounded-md transition-colors font-medium"
                >
                  <CheckCircle2 size={14} />
                  Resolver
                </button>
              )}
              {pendiente && onDescartar && (
                <button
                  type="button"
                  onClick={() => onDescartar(aviso)}
                  className="flex items-center gap-1 text-xs px-3 py-1.5 bg-brand-ghost text-brand-gray hover:bg-brand-silver/30 rounded-md transition-colors font-medium"
                >
                  <X size={14} />
                  Descartar
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AvisoCard;
